import { type LoadableResponse } from "api-types";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useFirebase, useThrowAsyncError } from "igfd-commons";

import { useEffect, useState } from "react";

import { activeTranslationOf } from "@dashboard/utils/activeTranslation";

import { useLanguage } from "../hooks";

// This type corresponds to documents in the firestore collection "metadata-countries"
type MetadataCountry = {
  countryNamesByLanguage: {
    [language: string]: string;
  };
  countryCode: string;
  appStatus: "listed"; // unlisted countries aren't returned by this hook.
  currencyCode: string;
  currencyMinorUnits: number;
  currencySymbol: string;
};

export type CountryNameAndCode = {
  name: string;
  code: string;
};

export const useCountries = (): LoadableResponse<CountryNameAndCode[]> => {
  const language = useLanguage();
  const firebase = useFirebase();

  const [countries, setCountries] = useState<CountryNameAndCode[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const throwAsyncError = useThrowAsyncError();

  useEffect(() => {
    setIsLoading(true);
    getDocs(
      query(
        collection(firebase.firestore, "metadata-countries"),
        where("appStatus", "==", "listed")
      )
    )
      .then((docs) => {
        const countryNamesAndCodes = docs.docs.map((doc) => {
          const metadataCountry = doc.data() as MetadataCountry;
          return {
            name: activeTranslationOf(
              metadataCountry.countryNamesByLanguage,
              language
            )?.translation!,
            code: metadataCountry.countryCode,
          } as CountryNameAndCode;
        });
        setCountries(countryNamesAndCodes);
      })
      .catch(throwAsyncError)
      .finally(() => setIsLoading(false));
  }, [language]);

  return {
    isLoading,
    data: countries,
  } as LoadableResponse<CountryNameAndCode[]>;
};
